// The mascot's place on the reveal and the podium: a reserved, fixed-size box so a character chosen later can take it
// without anything around it moving. Until then the Pulse stands in, in the mood of the moment, and nothing else.
//
//   right   spike (a burst and sparks)       wrong   jolt (the defib shock)
//   partial steady (neither a win nor a miss) none    flat (the soft flatline)
//   podium  crown
//
// The box is `size` × `size` px (square: the most neutral frame for whatever character arrives). The Pulse paints
// across its width and may spark above it, so the box never clips. Decorative unless `label` is given.
//
// Under the medsnapp look (3 Nov 2026) the box holds MedSnapp's cast instead (CastMascot, same box): the organ character
// of the question's category (the `category` prop, else the current question's from the game store), and Beat, crowned,
// on the podium. Every other look keeps the Pulse exactly as before.
import { Pulse, type PulseMood } from './Pulse';
import { CastMascot } from './CastMascot';
import { DEFAULT_CAST, mascotFor } from './cast';
import { useLook } from '@/look';
import { useGame } from '@/store/game';

export type MascotMoment = 'right' | 'wrong' | 'partial' | 'none' | 'podium';

export const MASCOT_MOOD: Readonly<Record<MascotMoment, PulseMood>> = { right: 'spike', wrong: 'jolt', partial: 'steady', none: 'flat', podium: 'crown' };
const TONE: Readonly<Record<MascotMoment, string>> = { right: 'text-correct', wrong: 'text-wrong', partial: 'text-cart', none: 'text-ink-3', podium: 'text-cart' };

/** The cast member the medsnapp look shows for a moment: Beat on the podium, else the category's organ character. */
export const castFor = (moment: MascotMoment, category?: string): string => (moment === 'podium' ? DEFAULT_CAST : mascotFor(category));

export function MascotSlot({ moment, size = 64, className = '', label, category }: { moment: MascotMoment; size?: number; className?: string; label?: string; category?: string }) {
  const { look } = useLook();
  const current = useGame((s) => s.snapshot?.question?.category);
  if (look === 'medsnapp') {
    return (
      <span data-part="mascot-slot" data-moment={moment} data-cast-slot="" {...(label ? {} : { 'aria-hidden': true as const })}
        className={`relative inline-flex shrink-0 items-center justify-center ${className}`}
        style={{ width: size, height: size, overflow: 'visible' }}>
        <CastMascot name={castFor(moment, category ?? current)} mood={moment} size={size} label={label} />
      </span>
    );
  }
  const a11y = label ? {} : { 'aria-hidden': true as const };
  return (
    <span data-part="mascot-slot" data-moment={moment} {...a11y}
      className={`relative inline-flex shrink-0 items-center justify-center ${TONE[moment]} ${className}`}
      style={{ width: size, height: size, overflow: 'visible' }}>
      <Pulse mood={MASCOT_MOOD[moment]} width={size} height={Math.round(size * 0.42)} glow={moment === 'right' || moment === 'podium'} label={label} />
    </span>
  );
}
